
import React, { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, CreditCard, Wallet } from 'lucide-react';
import WalletOverview from '@/components/admin/payments/WalletOverview';
import TransactionHistory from '@/components/admin/payments/TransactionHistory';
import PaymentsManagement from '@/components/admin/PaymentsManagement';
import { useStripePayment } from '@/hooks/stripe/useStripePayment';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const AdminPayments = () => {
  const navigate = useNavigate();
  const { isLoading, user, handleLogout } = useAdminSettings();
  const [activeTab, setActiveTab] = useState('wallet');
  const { lastPaymentResult } = useStripePayment();

  return (
    <AdminLayout
      isLoading={isLoading}
      onLogout={handleLogout}
      userRole={user?.role}
      activeTab="payments"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Payment & Wallet Management</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        {lastPaymentResult?.success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CreditCard className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">Payment Successful</AlertTitle>
            <AlertDescription className="text-green-700">
              Your payment was processed successfully. Your wallet has been updated.
            </AlertDescription>
          </Alert>
        )}
        
        {lastPaymentResult?.error && (
          <Alert className="mb-6 bg-red-50 border-red-200">
            <CreditCard className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-800">Payment Failed</AlertTitle>
            <AlertDescription className="text-red-700">
              {lastPaymentResult.error}
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full md:w-auto grid-cols-3">
            <TabsTrigger value="wallet">Wallet & Balance</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="payments">Payment History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="wallet" className="space-y-4">
            <WalletOverview adminId={user?.id || 'admin-1'} />
          </TabsContent>
          
          <TabsContent value="transactions" className="space-y-4">
            <TransactionHistory adminId={user?.id || 'admin-1'} />
          </TabsContent>
          
          <TabsContent value="payments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Wallet className="mr-2 h-5 w-5" /> Payment Methods
                </CardTitle>
                <CardDescription>
                  Manage your Stripe payment settings and methods
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-md flex items-center justify-center text-white">
                          <CreditCard className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-medium">Stripe Integration</h3>
                          <p className="text-sm text-muted-foreground">Process payments securely with Stripe</p>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => navigate('/admin/settings')}>
                        Configure
                      </Button>
                    </div>
                  </div>
                  
                  <PaymentsManagement instanceId={user?.instanceId || 'instance-1'} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
